import { useMutation, useQuery } from '@apollo/client'
import { COUNTRY_LIST, PROVINCES_LIST } from 'constants'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useOutletContext } from 'react-router-dom'

import {
  Button,
  CardBox,
  Fields,
  FormControl,
  InputLabel,
  MenuItem,
  Row,
  Select,
  Stack,
  Text,
  useAlert,
} from '../../components'
import { validations } from '../../components/form'
import { mutations, queries } from '../../graphql'
import { parseAndSetFormErrors } from '../../utils'

export function DonorDetailsForm() {
  const {
    donorFundId,
    setHasUnsavedChanges: trackUnsavedChanges,
    profileFormRef,
    onSubmitSuccess: onSuccess,
    onSubmitFail: onFail,
  } = useOutletContext()

  const { data: { me } = {} } = useQuery(queries.user.me)
  const donorFund = me?.funds?.find((f) => f.id === donorFundId)
  const fundProfile = donorFund?.fundProfile
  const address = fundProfile?.address

  const { register, handleSubmit, formState, setError, getValues, setValue, watch, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      fundName: donorFund?.name ?? '',
      fundPhone: fundProfile?.phone ?? '',
      address: {
        country: address?.country ?? 'Canada',
        province: address?.province ?? '',
        city: address?.city ?? '',
        lineOne: address?.lineOne ?? '',
        lineTwo: address?.lineTwo ?? '',
        postalCode: address?.postalCode ?? '',
      },
    },
  })
  const { isDirty, isSubmitting, errors = {} } = formState
  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  const countryValue = watch('address.country')
  const provinceValue = watch('address.province')

  const effectiveCountry = countryValue ?? address?.country ?? 'Canada'
  const effectiveProvince = provinceValue ?? address?.province ?? ''

  useEffect(() => {
    trackUnsavedChanges(isDirty)
  }, [countryValue, isDirty, trackUnsavedChanges])

  const countryIsCanada = effectiveCountry === 'Canada' || effectiveCountry === 'CAN' || effectiveCountry === 'CA'

  const [updateProfile] = useMutation(mutations.user.updateProfile, {
    onCompleted: () => {
      setAlert({ message: 'Giving Fund details updated', severity: 'success' })
      reset(getValues())
      onSuccess?.()
    },
    onError: (serverErrors) => {
      parseAndSetFormErrors(serverErrors, setError)
      setAlert({ message: 'Update failed', severity: 'error' })
      onFail?.()
    },
    refetchQueries: [{ query: queries.user.me }],
  })

  const onSubmit = async (data) => {
    updateProfile({
      variables: {
        data: {
          fundName: data.fundName,
          address: data.address,
          fundPhone: data.fundPhone,
        },
      },
    })
  }

  const onInvalid = () => {
    onFail?.()
  }

  return (
    <CardBox>
      <Text.H4>Giving Fund</Text.H4>
      <Text.Body color="text.secondary" sx={{ mt: 2 }}>
        These settings belong to the currently selected Giving Fund and will change when you switch Giving Funds.
      </Text.Body>
      <br />
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} ref={profileFormRef}>
        <Stack>
          <Text.H6>Giving Fund Name</Text.H6>
          <Text.Body color="text.secondary">
            The name used to identify the Giving Fund within GiveWise. You can change this at any time.
          </Text.Body>
          <Fields.Text
            inputProps={{ 'data-testid': 'fundName' }}
            type="text"
            label="Giving Fund Name *"
            error={!!errors.fundName}
            helperText={errors?.fundName?.message}
            defaultValue={donorFund?.name}
            {...register('fundName', { ...validations.required })}
          />

          <Text.H6 sx={{ mt: 2 }}>Mailing Address</Text.H6>
          <Text.Body>Used on tax receipts.</Text.Body>
          <FormControl fullWidth>
            <InputLabel id="countryLabel">Country *</InputLabel>
            <Select
              inputProps={{ 'data-testid': 'country' }}
              value={effectiveCountry}
              onChange={(e) => setValue('address.country', e.target.value, { shouldValidate: true })}
              label="Country *"
              labelId="countryLabel"
              aria-label="Country *"
              error={!!errors.address?.country}
              helperText={errors?.address?.country?.message}
              defaultValue={address?.country ?? 'Canada'}
              {...register('address.country', { ...validations.required })}
            >
              {COUNTRY_LIST.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {countryIsCanada ? (
            <FormControl fullWidth>
              <InputLabel id="provinceLabel">Province *</InputLabel>
              <Select
                inputProps={{ 'data-testid': 'province' }}
                value={effectiveProvince}
                onChange={(e) => setValue('address.province', e.target.value, { shouldValidate: true })}
                label="Province *"
                labelId="provinceLabel"
                aria-label="Province *"
                error={!!errors.address?.province}
                helperText={errors?.address?.province?.message}
                defaultValue={address?.province}
                {...register('address.province', { ...validations.required })}
              >
                {PROVINCES_LIST.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Fields.Text
              inputProps={{ 'data-testid': 'province' }}
              type="text"
              label="Province / State *"
              error={!!errors.address?.province}
              helperText={errors?.address?.province?.message}
              defaultValue={address?.province}
              {...register('address.province', { ...validations.required })}
            />
          )}

          <Fields.Text
            inputProps={{ 'data-testid': 'city' }}
            type="text"
            label="City *"
            error={!!errors.address?.city}
            helperText={errors?.address?.city?.message}
            defaultValue={address?.city}
            {...register('address.city', { ...validations.required })}
          />
          <Fields.Text
            inputProps={{ 'data-testid': 'addrLineOne' }}
            type="text"
            label="Address Line One *"
            error={!!errors.address?.lineOne}
            helperText={errors?.address?.lineOne?.message}
            defaultValue={address?.lineOne}
            {...register('address.lineOne', { ...validations.required })}
          />
          <Fields.Text
            inputProps={{ 'data-testid': 'addrLineTwo' }}
            type="text"
            label="Address Line Two"
            error={!!errors.address?.lineTwo}
            helperText={errors?.address?.lineTwo?.message}
            defaultValue={address?.lineTwo}
            {...register('address.lineTwo')}
          />
          {countryIsCanada ? (
            <Fields.Text
              inputProps={{ 'data-testid': 'postalCode' }}
              type="text"
              label="Postal Code *"
              error={!!errors.address?.postalCode}
              helperText={errors?.address?.postalCode?.message}
              defaultValue={address?.postalCode}
              {...register('address.postalCode', { ...validations.required, ...validations.postalCode })}
            />
          ) : (
            <Fields.Text
              inputProps={{ 'data-testid': 'postalCode' }}
              type="text"
              label="Postal Code / Zip Code *"
              error={!!errors.address?.postalCode}
              helperText={errors?.address?.postalCode?.message}
              defaultValue={address?.postalCode}
              {...register('address.postalCode', { ...validations.required })}
            />
          )}

          <Text.H6 sx={{ mt: 2 }}>Phone Number</Text.H6>
          <Text.Body>The phone number for this Giving Fund.</Text.Body>
          <Fields.Text
            inputProps={{ 'data-testid': 'fundPhone' }}
            type="text"
            label="Phone *"
            error={!!errors.fundPhone}
            helperText={errors?.fundPhone?.message}
            defaultValue={fundProfile?.phone}
            {...register('fundPhone', { ...validations.required, ...validations.phoneNumber })}
          />

          <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
            <Button type="submit" disabled={isSubmitting} data-testid="updateDonorDetailsButton">
              Update Details
            </Button>
          </Row>
          <Alert {...alertProps} />
        </Stack>
      </form>
    </CardBox>
  )
}
